import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { subMonths, format } from "date-fns";
import { formatCurrency, getAppIcon, getAppColor } from "@/lib/utils";
import { Application, License } from "@shared/schema";

// Temporary workaround for TreeMap import issue
const TreeMap = ({ data, dataKey, nameKey, aspectRatio, stroke, fill, content }: any) => (
  <div className="flex flex-wrap gap-2">
    {data.map((item: any, index: number) => (
      <div 
        key={index}
        className="border border-white rounded-md p-2 flex flex-col items-center justify-center text-white text-center"
        style={{ 
          backgroundColor: item.color || '#2563eb',
          width: `${Math.max(100 * (item.value / Math.max(...data.map((d: any) => d.value))), 15)}%`,
          minHeight: '80px',
          flex: `0 0 ${Math.max(100 * (item.value / Math.max(...data.map((d: any) => d.value))), 15)}%`,
        }}
      >
        <div className="font-bold">{item.name}</div>
        <div className="text-sm">${item.value.toLocaleString()}</div>
      </div>
    ))}
  </div>
);

type GroupByOption = "category" | "department" | "vendor";

// Sample department mapping for demo purposes
const departmentMapping: Record<string, string> = {
  "Collaboration": "IT",
  "Productivity": "IT",
  "Communication": "IT",
  "Design": "Marketing",
  "Development": "Engineering",
  "Marketing": "Marketing",
  "File Storage": "Operations",
  "Analytics": "Analytics",
  "CRM": "Sales",
  "Customer Support": "Support",
  "Security": "IT",
  "HR": "HR",
  "Finance": "Finance",
  "Database": "Engineering"
};

interface BudgetUtilizationProps {
  startDate?: Date;
  endDate?: Date;
}

export default function BudgetUtilization({ 
  startDate = subMonths(new Date(), 3), 
  endDate = new Date() 
}: BudgetUtilizationProps) {
  const [groupBy, setGroupBy] = useState<GroupByOption>("category");
  
  const { data: applications, isLoading: appsLoading } = useQuery({
    queryKey: ['/api/applications'],
  });
  
  const { data: allLicenses, isLoading: licensesLoading } = useQuery({
    queryKey: ['/api/licenses'],
  });

  // Filter licenses active within the selected date range
  const licenses = (allLicenses as License[] | undefined)?.filter((license: License) => {
    const licStart = new Date(license.startDate);
    const licRenewal = license.renewalDate ? new Date(license.renewalDate) : null;
    return licStart <= endDate && (!licRenewal || licRenewal >= startDate);
  }) ?? [];

  const isLoading = appsLoading || licensesLoading;

  // Transform data for the chart
  const getChartData = () => {
    if (!applications || !licenses) return [];
    
    if (groupBy === "category") {
      // Group by category
      const categoryCosts: Record<string, number> = {};
      const categoryApps: Record<string, Application[]> = {};
      
      applications.forEach((app: Application) => {
        const category = app.category || 'Other';
        if (!categoryApps[category]) {
          categoryApps[category] = [];
        }
        categoryApps[category].push(app);
      });
      
      licenses.forEach((license: License) => {
        const app = applications.find((a: Application) => a.id === license.applicationId);
        const category = app?.category || 'Other';
        
        if (!categoryCosts[category]) {
          categoryCosts[category] = 0;
        }
        categoryCosts[category] += license.cost;
      });
      
      return Object.entries(categoryCosts).map(([category, cost]) => ({
        name: category,
        value: cost,
        apps: categoryApps[category] || [],
        children: (categoryApps[category] || []).map(app => {
          const appLicenses = licenses.filter(l => l.applicationId === app.id);
          const appCost = appLicenses.reduce((sum, l) => sum + l.cost, 0);
          return {
            name: app.name,
            value: appCost,
            logo: app.logo
          };
        })
      })).sort((a, b) => b.value - a.value);
      
    } else if (groupBy === "department") {
      // Group by department
      const departments: Record<string, {cost: number, categories: string[]}> = {};
      
      licenses.forEach((license: License) => {
        const app = applications.find((a: Application) => a.id === license.applicationId);
        const category = app?.category || 'Other';
        const department = departmentMapping[category] || 'Other';
        
        if (!departments[department]) {
          departments[department] = { cost: 0, categories: [] };
        }
        departments[department].cost += license.cost;
        
        if (!departments[department].categories.includes(category)) {
          departments[department].categories.push(category);
        }
      });
      
      return Object.entries(departments).map(([department, { cost, categories }]) => ({
        name: department,
        value: cost,
        categories
      })).sort((a, b) => b.value - a.value);
      
    } else {
      // Group by vendor
      const vendorCosts: Record<string, number> = {};
      const vendorApps: Record<string, Application[]> = {};
      
      applications.forEach((app: Application) => {
        const vendor = app.vendor || 'Other';
        if (!vendorApps[vendor]) {
          vendorApps[vendor] = [];
        }
        vendorApps[vendor].push(app);
      });
      
      licenses.forEach((license: License) => {
        const app = applications.find((a: Application) => a.id === license.applicationId);
        const vendor = app?.vendor || 'Other';
        
        if (!vendorCosts[vendor]) {
          vendorCosts[vendor] = 0;
        }
        vendorCosts[vendor] += license.cost;
      });
      
      return Object.entries(vendorCosts).map(([vendor, cost]) => ({
        name: vendor,
        value: cost,
        apps: vendorApps[vendor] || []
      })).sort((a, b) => b.value - a.value);
    }
  };
  
  const COLORS = [
    '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe',
    '#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5',
    '#f43f5e', '#fb7185', '#fda4af', '#fecdd3', '#fee2e2',
    '#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#fef3c7',
    '#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0', '#f1f5f9',
  ];
  
  const customTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="bg-white p-3 border border-slate-200 shadow-sm rounded-md">
          <p className="font-medium text-slate-800">{data.name}</p>
          <p className="text-sm mt-1 font-medium text-primary">
            {formatCurrency(data.value)}
          </p>
          {data.apps && data.apps.length > 0 && (
            <p className="text-sm mt-1 text-slate-500">
              {data.apps.length} applications
            </p>
          )}
          {data.categories && data.categories.length > 0 && (
            <p className="text-sm mt-1 text-slate-500">
              Categories: {data.categories.join(', ')}
            </p>
          )}
        </div>
      );
    }
    return null;
  };
  
  const getTotalCost = () => {
    if (!licenses) return 0;
    return licenses.reduce((sum: number, license: License) => sum + license.cost, 0);
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="text-xl font-bold text-slate-900">Budget Utilization</CardTitle>
            <p className="text-sm text-slate-600 mt-1">
              Period: {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
            </p>
          </div>
          <Select defaultValue={groupBy} onValueChange={(value) => setGroupBy(value as GroupByOption)}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Group by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="category">By Category</SelectItem>
              <SelectItem value="department">By Department</SelectItem>
              <SelectItem value="vendor">By Vendor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-slate-900">{formatCurrency(getTotalCost())}</h3>
            <p className="text-sm font-medium text-slate-600">Total Spending</p>
          </div>
          <div className="text-center">
            <h3 className="text-2xl font-bold text-slate-900">{getChartData().length}</h3>
            <p className="text-sm font-medium text-slate-600">
              {groupBy === "category" 
                ? "Categories" 
                : groupBy === "department" 
                  ? "Departments" 
                  : "Vendors"}
            </p>
          </div>
        </div>
        
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-64 w-full rounded-lg" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          </div>
        ) : getChartData().length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <div className="w-6 h-6 rounded-full bg-slate-300"></div>
            </div>
            <p className="text-sm font-medium">No budget data available</p>
            <p className="text-xs text-slate-400 mt-1">Add some licenses to see budget allocation</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Modern Card-based visualization instead of TreeMap */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {getChartData().slice(0, 9).map((item, index) => {
                const percentage = Math.round((item.value / getTotalCost()) * 100);
                return (
                  <div 
                    key={index}
                    className="group relative overflow-hidden bg-gradient-to-br from-white to-slate-50 p-4 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300"
                  >
                    {/* Color indicator */}
                    <div 
                      className="absolute top-0 left-0 w-full h-1 rounded-t-xl"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    
                    {/* Content */}
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <h4 className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2 flex-1 mr-2">
                          {item.name}
                        </h4>
                        <span 
                          className="text-xs font-bold px-2 py-1 rounded-full text-white"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        >
                          {percentage}%
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-lg font-bold text-slate-900">
                          {formatCurrency(item.value)}
                        </p>
                        
                        {/* Progress bar */}
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full transition-all duration-500"
                            style={{ 
                              backgroundColor: COLORS[index % COLORS.length],
                              width: `${percentage}%`,
                              minWidth: '4px'
                            }}
                          />
                        </div>
                        
                        {/* Additional info */}
                        {(item as any).apps && (item as any).apps.length > 0 && (
                          <p className="text-xs text-slate-500 mt-1">
                            {(item as any).apps.length} application{(item as any).apps.length !== 1 ? 's' : ''}
                          </p>
                        )}
                        
                        {(item as any).categories && (item as any).categories.length > 0 && (
                          <p className="text-xs text-slate-500 mt-1">
                            {(item as any).categories.slice(0, 2).join(', ')}
                            {(item as any).categories.length > 2 && (
                              <span className="ml-1">+{(item as any).categories.length - 2} more</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Show more items if there are many */}
            {getChartData().length > 9 && (
              <div className="text-center pt-4">
                <p className="text-sm text-slate-500">
                  Showing top 9 of {getChartData().length} {groupBy === "category" 
                    ? "categories" 
                    : groupBy === "department" 
                      ? "departments" 
                      : "vendors"}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
